import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Gift, Copy, Users, Sparkles, Check, Loader2, Share2 } from "lucide-react";

/**
 * Guest referral card — shown inside `/guest/dashboard`.
 * On demand the user generates their personal code (max 10 friends/year).
 * When a friend actually checks in, the referrer is credited a 5% code via email.
 */
export default function ReferralCard({ guestName }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const [status, setStatus] = useState(null); // {code, uses_year, remaining_year, max_uses_year, percent_off, claims}
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = () =>
    api.get("/guest/referral/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/guest/referral/generate", {});
      setStatus(r.data);
      toast.success(IT ? "Codice referral creato · controlla anche l'email" : "Referral code created · check your email");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setGenerating(false); }
  };

  const copy = async (txt, label) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success(label);
    } catch { toast.error(IT ? "Copia non riuscita" : "Copy failed"); }
  };

  if (loading) return null;

  const percentOff = status?.percent_off ?? 5;
  const maxUses = status?.max_uses_year ?? 10;
  const usesYear = status?.uses_year ?? 0;
  const remaining = status?.remaining_year ?? maxUses;
  const pct = Math.min(100, Math.round((usesYear / maxUses) * 100));
  const shareUrl = status?.code
    ? `${window.location.origin}/strutture?ref=${encodeURIComponent(status.code)}`
    : "";

  return (
    <section className="mb-10" data-testid="referral-card">
      <div className="flex items-baseline gap-3 mb-3">
        <Users className="w-5 h-5 text-primary self-center"/>
        <h2 className="font-display text-2xl">{IT ? "Invita un amico" : "Invite a friend"}</h2>
        <span className="text-xs text-muted-foreground">· {IT ? `${percentOff}% per entrambi` : `${percentOff}% for both`}</span>
      </div>

      {!status?.code ? (
        <div className="border border-primary/30 rounded-lg p-6 bg-gradient-to-r from-primary/5 via-sage/5 to-primary/5" data-testid="referral-empty">
          <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-center">
            <div>
              <div className="uppercase tracking-[0.2em] text-[10px] text-primary font-semibold flex items-center gap-1.5 mb-1">
                <Gift className="w-3 h-3"/>{IT ? "Sblocca il tuo codice" : "Unlock your code"}
              </div>
              <div className="font-display text-xl mb-1">
                {IT ? `Ogni amico prenota con -${percentOff}% e ti regala -${percentOff}%` : `Each friend books at -${percentOff}% and gifts you -${percentOff}%`}
              </div>
              <p className="text-xs text-muted-foreground">
                {IT
                  ? `Il tuo bonus viene accreditato dopo il check-in dell'amico (validità 6 mesi). Massimo ${maxUses} amici all'anno.`
                  : `Your bonus is credited after the friend's check-in (valid 6 months). Up to ${maxUses} friends per year.`}
              </p>
            </div>
            <Button
              onClick={generate}
              disabled={generating}
              className="bg-primary text-primary-foreground hover:bg-primary/90 lift w-full sm:w-auto"
              data-testid="referral-generate-btn"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/> : <Sparkles className="w-4 h-4 mr-1.5"/>}
              {IT ? "Genera il mio codice" : "Generate my code"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="border border-primary/30 rounded-lg overflow-hidden bg-card" data-testid="referral-active">
          <div className="bg-gradient-to-r from-primary/10 via-sage/10 to-primary/5 px-6 py-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="uppercase tracking-[0.2em] text-[10px] text-primary font-semibold">
                {IT ? "Il tuo codice" : "Your code"}
              </div>
              <button
                onClick={() => copy(status.code, IT ? "Codice copiato" : "Code copied")}
                className="mt-1 inline-flex items-center gap-2 font-mono text-xl tracking-widest text-primary hover:opacity-80"
                data-testid="referral-code-copy"
              >
                {status.code}<Copy className="w-3.5 h-3.5 opacity-60"/>
              </button>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {IT ? "Amici invitati (12 mesi)" : "Friends invited (12 mo.)"}
              </div>
              <div className="text-2xl font-display" data-testid="referral-uses-count">
                {usesYear}<span className="text-sm text-muted-foreground">/{maxUses}</span>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 space-y-4">
            {/* Usage bar */}
            <div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${remaining > 0 ? "bg-primary" : "bg-destructive"}`}
                  style={{ width: `${pct}%` }}
                  data-testid="referral-usage-bar"
                />
              </div>
              <div className="text-xs text-muted-foreground mt-1.5">
                {remaining > 0
                  ? (IT ? `Ti restano ${remaining} inviti attivi` : `${remaining} invites remaining`)
                  : (IT ? "Limite annuale raggiunto — si ricarica giorno per giorno" : "Yearly limit reached — resets day by day")}
              </div>
            </div>

            {/* Share link */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1">
                <Share2 className="w-3 h-3"/>{IT ? "Link da condividere" : "Share link"}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  readOnly value={shareUrl}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 min-w-[200px] text-xs font-mono bg-muted/40 border border-border rounded px-2 py-1.5"
                  data-testid="referral-share-input"
                />
                <Button
                  variant="outline" size="sm" className="gap-1.5"
                  onClick={() => copy(shareUrl, IT ? "Link copiato" : "Link copied")}
                  data-testid="referral-share-copy"
                >
                  <Copy className="w-3.5 h-3.5"/>{IT ? "Copia" : "Copy"}
                </Button>
                {navigator.share && (
                  <Button
                    size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5"
                    onClick={() => navigator.share({
                      title: "HospitalityHub", url: shareUrl,
                      text: IT
                        ? `Prenota con il mio codice e risparmia ${percentOff}%: ${status.code}`
                        : `Book with my code and save ${percentOff}%: ${status.code}`,
                    })}
                    data-testid="referral-share-native"
                  >
                    <Share2 className="w-3.5 h-3.5"/>{IT ? "Condividi" : "Share"}
                  </Button>
                )}
              </div>
            </div>

            {/* Claims list */}
            {status.claims?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                  {IT ? "Storico inviti" : "Invite history"}
                </div>
                <ul className="space-y-1.5">
                  {status.claims.map((c, i) => (
                    <li key={i} className="flex items-center justify-between text-xs border border-border/60 rounded px-3 py-2" data-testid={`referral-claim-${i}`}>
                      <span className="font-mono text-muted-foreground">{c.friend_email_masked}</span>
                      {c.status === "earned" ? (
                        <span className="inline-flex items-center gap-1 text-sage">
                          <Check className="w-3 h-3"/>
                          {c.credited_promo_code ? (
                            <button
                              onClick={() => copy(c.credited_promo_code, IT ? "Codice copiato" : "Code copied")}
                              className="font-mono hover:underline"
                              data-testid={`referral-earned-code-${i}`}
                            >{c.credited_promo_code}</button>
                          ) : (IT ? "Accreditato" : "Credited")}
                        </span>
                      ) : (
                        <span className="text-amber-600">{IT ? "In attesa del check-in" : "Awaiting check-in"}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
